import { z } from 'zod'

import { OrderCartItemStatus, OrderCartStatus } from '../constants/orderCart'
import { dbEntitySchema } from './system'
import { InventoryTierMode, InventoryType } from '../constants/inventory'
import { bookingFeeSchema } from './booking'

export const orderCartItemInputSchema = z
  .object({
    inventoryId: z.string().optional(),
    productId: z.string().optional(),
    date: z.coerce.date().optional(),
    quantity: z.number().int().min(1),
    arrivalTime: z.coerce.date().nullish(),
    guests: z.number().int().min(1).nullish(),
  })
  .superRefine((val, ctx) => {
    if (val.inventoryId) return
    if (val.productId && val.date) return
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'inventoryId or productId+date is required',
    })
  })

export const orderCartCreateArgsSchema = z.object({
  items: z.array(orderCartItemInputSchema).min(1),
  previousCartId: z.string().optional(),
})

export const orderCartItemSchema = z.object({
  inventoryId: z.string(),
  productId: z.string().nullish(),
  date: z.date().nullish(),
  quantity: z.number().int().min(1),
  arrivalTime: z.date().nullish(),
  guests: z.number().int().min(1).nullish(),
  status: z.nativeEnum(OrderCartItemStatus),
  availableQuantity: z.number().int().min(0).nullish(),
  holdId: z.string().nullish(),
  inventory: z
    .object({
      id: z.string(),
      name: z.string().nullish(),
      description: z.string().nullish(),
      type: z.nativeEnum(InventoryType).nullish(),
      date: z.date().nullish(),
      venue: z.string().nullish(),
      event: z.string().nullish(),
      price: z.number().nullish(),
      paymentType: z.string().nullish(),
      depositAmount: z.number().nullish(),
      tax: z.number().nullish(),
      tip: z.number().nullish(),
      userPaysProcessing: z.boolean().nullish(),
      fee: z
        .object({
          enable: z.boolean().nullish(),
          fees: z.array(z.any()).nullish(),
        })
        .nullish(),
      purchaseMin: z.number().int().min(1).nullish(),
      purchaseMax: z.number().int().min(1).nullish(),
      quantityTotal: z.number().int().min(0).nullish(),
      start: z.date().nullish(),
      end: z.date().nullish(),
      tier: z
        .object({
          mode: z.nativeEnum(InventoryTierMode).nullish(),
          activeLevel: z.string().nullish(),
          levels: z.array(
            z.object({
              _id: z.string(),
              price: z.number().nullish(),
              quantity: z.number().nullish(),
              minGuests: z.number().int().min(1).nullish(),
              maxGuests: z.number().int().min(1).nullish(),
            })
          ),
        })
        .nullish(),
    })
    .nullish(),
})

export const cartPricingBreakdownSchema = z.object({
  subtotal: z.number().nullish(),
  tax: z.number().nullish(),
  taxPercent: z.number().nullish(),
  tip: z.number().nullish(),
  tipPercent: z.number().nullish(),
  taxAndTip: z.number().nullish(),
  feeTotal: z.number().nullish(),
  processingFeeTotal: z.number().nullish(),
  processingFeePercent: z.number().nullish(),
  total: z.number().nullish(),
  dueOnArrival: z.number().nullish(),
  fees: z.array(bookingFeeSchema).nullish(),
  processingFees: z.array(bookingFeeSchema).nullish(),
})

export const orderCartSchema = dbEntitySchema.extend({
  user: z.string().nullish(),
  prospect: z.string().nullish(),
  venueId: z.string().nullish(),
  order: z.string().nullish(),
  status: z.nativeEnum(OrderCartStatus),
  expiresAt: z.date().nullish(),
  pricingBreakdown: cartPricingBreakdownSchema.nullish(),
  items: z.array(orderCartItemSchema),
})

export const orderCartGetByIdParamsSchema = z.object({
  cartId: z.string(),
})

export const orderCartRetireByIdParamsSchema = z.object({
  cartId: z.string(),
})
